/**
 * QuestionRenderer - Factory class for rendering all discovery question types
 * Manages internal state and provides methods for validation and response formatting
 */
export class QuestionRenderer {
    constructor(container) {
        this.container = container;
        this.currentQuestion = null;
        this.currentResponse = null;
        this.questionElement = null;
        this.orchestrator = null; // Set by orchestrator for cross-question filtering
    }

    /**
     * Set the orchestrator reference for cross-question filtering.
     * @param {Object} orchestrator - The discovery orchestrator instance
     */
    setOrchestrator(orchestrator) {
        this.orchestrator = orchestrator;
    }

    /**
     * Get options for a question, applying any dynamic filtering.
     * Supports filterOptionsFromQuestion config to show only options
     * that were selected in a referenced question.
     * @param {Object} question - Question configuration
     * @returns {Array} Filtered options array
     * @private
     */
    _getFilteredOptions(question) {
        const config = question.configuration;
        if (!config.filterOptionsFromQuestion || !config.options) {
            return config.options || [];
        }

        // Get the source question's response
        const sourceResponse = this.orchestrator?.getResponseForQuestion(config.filterOptionsFromQuestion);
        if (!sourceResponse?.structuredData?.selectedOptions) {
            return config.options; // Fallback to all options if no response yet
        }

        // Filter to only values selected in source question
        const selectedValues = sourceResponse.structuredData.selectedOptions.map(o => o.value);
        return config.options.filter(opt => selectedValues.includes(opt.value));
    }

    /**
     * Main render method - determines question type and delegates to specific renderer
     * @param {Object} question - Question object from API
     * @param {Object} existingResponse - Previous response if navigating back
     */
    render(question, existingResponse = null) {
        this.currentQuestion = question;
        this.currentResponse = existingResponse || this._initializeResponse(question.questionType);

        // Clear container
        this.container.innerHTML = '';

        // Create question wrapper
        const wrapper = document.createElement('div');
        wrapper.className = 'discovery__question';
        wrapper.dataset.questionId = question.id;
        wrapper.dataset.questionType = question.questionType;

        // Render question text
        const questionTextEl = document.createElement('div');
        questionTextEl.className = 'discovery__question-text';
        questionTextEl.textContent = question.questionText;
        wrapper.appendChild(questionTextEl);

        // Render help text if exists
        if (question.helpText) {
            const helpTextEl = document.createElement('div');
            helpTextEl.className = 'discovery__question-help';
            helpTextEl.textContent = question.helpText;
            wrapper.appendChild(helpTextEl);
        }

        // Create response area
        const responseArea = document.createElement('div');
        responseArea.className = `discovery__response discovery__response--${question.questionType.toLowerCase().replace(/_/g, '-')}`;

        // Delegate to specific renderer
        switch (question.questionType) {
            case 'SINGLE_CHOICE':
                this._renderSingleChoice(responseArea, question.configuration, existingResponse);
                break;
            case 'MULTIPLE_CHOICE':
                this._renderMultipleChoice(responseArea, question.configuration, existingResponse);
                break;
            case 'RATING_SCALE':
                this._renderRatingScale(responseArea, question.configuration, existingResponse);
                break;
            case 'TEXT':
                this._renderText(responseArea, question.configuration, existingResponse);
                break;
            case 'MATRIX':
                this._renderMatrix(responseArea, question.configuration, existingResponse);
                break;
            case 'HYBRID_SINGLE_TEXT':
                this._renderHybridSingleText(responseArea, question.configuration, existingResponse);
                break;
            case 'HYBRID_MULTIPLE_TEXT':
                this._renderHybridMultipleText(responseArea, question.configuration, existingResponse);
                break;
            case 'INSTRUCTION_CARD':
                this._renderInstructionCard(responseArea, question.configuration);
                break;
            case 'YES_NO':
                this._renderYesNo(responseArea, question.configuration, existingResponse);
                break;
            default:
                console.error('Unknown question type:', question.questionType);
        }

        wrapper.appendChild(responseArea);
        this.container.appendChild(wrapper);
        this.questionElement = wrapper;
    }

    /**
     * SINGLE_CHOICE renderer - Radio buttons
     */
    _renderSingleChoice(container, config, existingResponse) {
        const options = this._getFilteredOptions(this.currentQuestion);

        options.forEach((option, index) => {
            const optionWrapper = document.createElement('div');
            optionWrapper.className = 'discovery__option';

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'single-choice';
            input.id = `option-${index}`;
            input.value = option.value;

            // Check if this was previously selected
            if (existingResponse?.structuredData?.value === option.value) {
                input.checked = true;
                optionWrapper.classList.add('discovery__option--selected');
            }

            // Update selection state when changed
            input.addEventListener('change', () => {
                // Remove selected class from all options
                container.querySelectorAll('.discovery__option').forEach(opt => {
                    opt.classList.remove('discovery__option--selected');
                });

                // Add selected class to this option
                optionWrapper.classList.add('discovery__option--selected');

                this.currentResponse = {
                    structuredData: {
                        value: option.value,
                        label: option.label
                    }
                };
                // Trigger onChange callback if set
                if (this.onChange) {
                    this.onChange();
                }

                // Trigger auto-advance callback if set
                if (this.onAutoAdvance) {
                    this.onAutoAdvance();
                }
            });

            const label = document.createElement('label');
            label.htmlFor = `option-${index}`;

            if (option.sublabel) {
                const mainText = document.createElement('span');
                mainText.className = 'discovery__option-main';
                mainText.textContent = option.label;

                const subText = document.createElement('span');
                subText.className = 'discovery__option-sub';
                subText.textContent = option.sublabel;

                label.appendChild(mainText);
                label.appendChild(subText);
            } else {
                label.textContent = option.label;
            }

            // Make entire option clickable
            optionWrapper.addEventListener('click', () => {
                input.checked = true;
                input.dispatchEvent(new Event('change'));
            });

            optionWrapper.appendChild(input);
            optionWrapper.appendChild(label);
            container.appendChild(optionWrapper);
        });
    }

    /**
     * MULTIPLE_CHOICE renderer - Checkboxes
     */
    _renderMultipleChoice(container, config, existingResponse) {
        const options = config.options || [];
        const maxSelections = config.maxSelections;
        const selectedValues = existingResponse?.structuredData?.selectedOptions || [];

        options.forEach((option, index) => {
            const optionWrapper = document.createElement('div');
            optionWrapper.className = 'discovery__option';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.id = `option-${index}`;
            input.value = option.value;

            // Check if previously selected
            if (selectedValues.some(sel => sel.value === option.value)) {
                input.checked = true;
                optionWrapper.classList.add('discovery__option--selected');
            }

            // Update selection state when changed
            input.addEventListener('change', () => {
                const checked = container.querySelectorAll('input[type="checkbox"]:checked');

                // Enforce max selections if specified
                if (maxSelections && checked.length > maxSelections) {
                    input.checked = false;
                    this._showMessage(`Maximum ${maxSelections} selections allowed`, 'warning');
                    return;
                }

                // Toggle selected class for this option
                if (input.checked) {
                    optionWrapper.classList.add('discovery__option--selected');
                } else {
                    optionWrapper.classList.remove('discovery__option--selected');
                }

                // Update response
                const selectedOptions = Array.from(checked).map(cb => {
                    const opt = options.find(o => o.value === cb.value);
                    return { value: cb.value, label: opt.label };
                });

                this.currentResponse = {
                    structuredData: { selectedOptions }
                };

                // Trigger onChange callback if set
                if (this.onChange) {
                    this.onChange();
                }
            });

            const label = document.createElement('label');
            label.htmlFor = `option-${index}`;

            if (option.sublabel) {
                const mainText = document.createElement('span');
                mainText.className = 'discovery__option-main';
                mainText.textContent = option.label;

                const subText = document.createElement('span');
                subText.className = 'discovery__option-sub';
                subText.textContent = option.sublabel;

                label.appendChild(mainText);
                label.appendChild(subText);
            } else {
                label.textContent = option.label;
            }

            // Make entire option clickable
            optionWrapper.addEventListener('click', (e) => {
                // Don't toggle if clicking on the checkbox or label (they handle themselves)
                if (e.target === input || label.contains(e.target)) return;

                input.checked = !input.checked;
                input.dispatchEvent(new Event('change'));
            });

            optionWrapper.appendChild(input);
            optionWrapper.appendChild(label);
            container.appendChild(optionWrapper);
        });
    }

    /**
     * RATING_SCALE renderer - Clickable button scale
     */
    _renderRatingScale(container, config, existingResponse) {
        const scale = config.scale || { min: 1, max: 5 };
        const labels = scale.labels || {};

        const scaleWrapper = document.createElement('div');
        scaleWrapper.className = 'discovery__scale';

        // Buttons row - horizontal clickable buttons
        const buttonsRow = document.createElement('div');
        buttonsRow.className = 'discovery__scale-buttons';

        // Create a button for each scale value
        for (let i = scale.min; i <= scale.max; i++) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'discovery__scale-button';
            button.dataset.value = i;
            button.textContent = i.toString();

            // Mark as selected if this is the existing response
            if (existingResponse?.structuredData?.value === i.toString()) {
                button.classList.add('discovery__scale-button--selected');
            }

            // Handle button click
            button.addEventListener('click', () => {
                // Remove selected class from all buttons
                buttonsRow.querySelectorAll('.discovery__scale-button').forEach(btn => {
                    btn.classList.remove('discovery__scale-button--selected');
                });

                // Add selected class to clicked button
                button.classList.add('discovery__scale-button--selected');

                // Update response
                this.currentResponse = {
                    structuredData: {
                        value: i.toString(),
                        label: labels[i] || i.toString()
                    }
                };
                // Trigger onChange callback if set
                if (this.onChange) {
                    this.onChange();
                }

                // Trigger auto-advance callback if set
                if (this.onAutoAdvance) {
                    this.onAutoAdvance();
                }
            });

            buttonsRow.appendChild(button);
        }

        scaleWrapper.appendChild(buttonsRow);

        // Labels container - display min/max labels below buttons
        const labelsContainer = document.createElement('div');
        labelsContainer.className = 'discovery__scale-labels';

        if (labels[scale.min]) {
            const minLabel = document.createElement('span');
            minLabel.className = 'discovery__scale-label discovery__scale-label--min';
            minLabel.textContent = labels[scale.min];
            labelsContainer.appendChild(minLabel);
        }

        if (labels[scale.max]) {
            const maxLabel = document.createElement('span');
            maxLabel.className = 'discovery__scale-label discovery__scale-label--max';
            maxLabel.textContent = labels[scale.max];
            labelsContainer.appendChild(maxLabel);
        }

        scaleWrapper.appendChild(labelsContainer);

        // Legend/key toggle (if configured)
        if (config.legend?.enabled) {
            const legendContainer = this._renderScaleLegend(config.legend, scale);
            scaleWrapper.appendChild(legendContainer);
        }

        container.appendChild(scaleWrapper);
    }

    /**
     * Render collapsible scale legend/key
     * @param {Object} legendConfig - Legend configuration
     * @param {Object} scale - Scale configuration (for auto-generate)
     * @returns {HTMLElement} Legend container
     */
    _renderScaleLegend(legendConfig, scale) {
        const container = document.createElement('div');
        container.className = 'discovery__scale-legend';

        // Toggle button
        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'discovery__scale-legend-toggle';

        const toggleText = document.createElement('span');
        toggleText.className = 'discovery__scale-legend-text';
        toggleText.textContent = legendConfig.title || 'Rating Guide';

        // Use SVG sprite icon
        const toggleIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        toggleIcon.setAttribute('width', '12');
        toggleIcon.setAttribute('height', '12');
        toggleIcon.classList.add('discovery__scale-legend-icon');
        const useEl = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        useEl.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#icon-arrowhead');
        toggleIcon.appendChild(useEl);

        toggleBtn.appendChild(toggleText);
        toggleBtn.appendChild(toggleIcon);
        container.appendChild(toggleBtn);

        // Legend content (collapsible)
        const content = document.createElement('div');
        content.className = 'discovery__scale-legend-content';

        // Get items (from config or auto-generate)
        const items = legendConfig.items || this._generateLegendItems(scale);

        // Create legend grid (CSS :has() handles 2 vs 3 column layout automatically)
        const grid = document.createElement('div');
        grid.className = 'discovery__scale-legend-grid';

        items.forEach(item => {
            const row = document.createElement('div');
            row.className = 'discovery__scale-legend-row';

            const valueEl = document.createElement('span');
            valueEl.className = 'discovery__scale-legend-value';
            valueEl.textContent = item.value;

            const labelEl = document.createElement('span');
            labelEl.className = 'discovery__scale-legend-label';
            labelEl.textContent = item.label;

            row.appendChild(valueEl);
            row.appendChild(labelEl);

            // Optional description
            if (item.description) {
                const descEl = document.createElement('span');
                descEl.className = 'discovery__scale-legend-desc';
                descEl.textContent = item.description;
                row.appendChild(descEl);
            }

            grid.appendChild(row);
        });

        content.appendChild(grid);
        container.appendChild(content);

        // Set initial state
        const isExpanded = legendConfig.defaultExpanded !== false;
        container.classList.toggle('discovery__scale-legend--expanded', isExpanded);
        toggleIcon.style.transform = isExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
        toggleBtn.setAttribute('aria-expanded', isExpanded);

        // Toggle handler
        toggleBtn.addEventListener('click', () => {
            const nowExpanded = container.classList.toggle('discovery__scale-legend--expanded');
            toggleIcon.style.transform = nowExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
            toggleBtn.setAttribute('aria-expanded', nowExpanded);
        });

        return container;
    }

    /**
     * Auto-generate legend items from scale labels
     * @param {Object} scale - Scale configuration
     * @returns {Array} Legend items array
     */
    _generateLegendItems(scale) {
        const items = [];
        const labels = scale.labels || {};

        for (let i = scale.min; i <= scale.max; i++) {
            if (labels[i]) {
                items.push({ value: i, label: labels[i] });
            }
        }

        return items;
    }

    /**
     * TEXT renderer - Text area
     */
    _renderText(container, config, existingResponse) {
        // Single-line opt-in renders a real <input> so the field
        // conforms to one line with no resize handle or min-height.
        const textarea = config.singleLine
            ? document.createElement('input')
            : document.createElement('textarea');
        if (config.singleLine) {
            textarea.type = 'text';
        }
        textarea.className = 'discovery__text-input';
        textarea.placeholder = config.placeholder || 'Enter your response...';

        if (config.maxLength) {
            textarea.maxLength = config.maxLength;
        }

        if (!config.singleLine) {
            textarea.rows = config.multiline ? 5 : 1;
        }

        if (existingResponse?.textData) {
            textarea.value = existingResponse.textData;
        }

        textarea.addEventListener('input', () => {
            this.currentResponse = {
                textData: textarea.value.trim()
            };
        });

        // Enter to submit: always for single-line inputs, Ctrl/Cmd+Enter for textareas
        textarea.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            const isSingle = config.singleLine;
            if (isSingle || e.ctrlKey || e.metaKey) {
                e.preventDefault();
                document.getElementById('wizard-nav-button')?.click();
            }
        });

        container.appendChild(textarea);

        // Character counter if maxLength specified
        if (config.maxLength) {
            const counter = document.createElement('div');
            counter.className = 'discovery__text-counter';
            counter.textContent = `${textarea.value.length} / ${config.maxLength}`;

            textarea.addEventListener('input', () => {
                counter.textContent = `${textarea.value.length} / ${config.maxLength}`;
            });

            container.appendChild(counter);
        }
    }

    /**
     * MATRIX renderer - Grid of questions
     */
    _renderMatrix(container, config, existingResponse) {
        const rows = config.rows || [];
        const columns = config.columns || [];
        const responses = existingResponse?.structuredData?.responses || {};

        // Wrapper for horizontal scrolling
        const wrapper = document.createElement('div');
        wrapper.className = 'discovery__matrix-wrapper';

        const table = document.createElement('table');
        table.className = 'discovery__matrix';

        // Header row
        const headerRow = document.createElement('tr');

        // Empty top-left cell
        const emptyHeader = document.createElement('th');
        emptyHeader.className = 'discovery__matrix-header discovery__matrix-header--empty';
        headerRow.appendChild(emptyHeader);

        // Column headers - center aligned, bottom aligned, with text wrapping
        columns.forEach(col => {
            const th = document.createElement('th');
            th.className = 'discovery__matrix-header';
            th.textContent = col.label;
            headerRow.appendChild(th);
        });
        table.appendChild(headerRow);

        // Data rows
        rows.forEach(row => {
            const tr = document.createElement('tr');
            tr.className = 'discovery__matrix-row';

            // Row label - right aligned with text wrapping
            const rowLabel = document.createElement('td');
            rowLabel.className = 'discovery__matrix-row-label';
            rowLabel.textContent = row.label;
            tr.appendChild(rowLabel);

            // Data cells with checkboxes (radio buttons styled as checkboxes)
            columns.forEach(col => {
                const td = document.createElement('td');
                td.className = 'discovery__matrix-cell';

                const input = document.createElement('input');
                input.type = 'radio';
                input.className = 'discovery__matrix-checkbox';
                input.name = `matrix-${row.value}`;
                input.value = col.value;

                // Check if previously selected
                if (responses[row.value]?.value === col.value) {
                    input.checked = true;
                }

                input.addEventListener('change', () => {
                    if (!this.currentResponse.structuredData) {
                        this.currentResponse.structuredData = { responses: {} };
                    }
                    this.currentResponse.structuredData.responses[row.value] = {
                        value: col.value,
                        label: col.label
                    };
                });

                td.appendChild(input);
                tr.appendChild(td);
            });

            table.appendChild(tr);
        });

        wrapper.appendChild(table);
        container.appendChild(wrapper);
    }

    /**
     * Create conditional text field for hybrid question types
     * @param {string} placeholder - Placeholder text for the input
     * @param {Object} existingResponse - Existing response data
     * @returns {{ wrapper: HTMLElement, input: HTMLElement }} The wrapper and input elements
     * @private
     */
    _createConditionalTextField(placeholder, existingResponse) {
        const wrapper = document.createElement('div');
        wrapper.className = 'discovery__option discovery__option--other';
        wrapper.setAttribute('data-text-field', 'true');

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'discovery__other-text';
        input.placeholder = placeholder;

        if (existingResponse?.textData) {
            input.value = existingResponse.textData;
        }

        input.addEventListener('input', () => {
            if (!this.currentResponse) {
                this.currentResponse = {};
            }
            this.currentResponse.textData = input.value.trim();
        });

        wrapper.appendChild(input);
        return { wrapper, input };
    }

    /**
     * Clear the conditional text field and response
     * @param {HTMLElement} input - The text input element
     * @private
     */
    _clearConditionalTextField(input) {
        input.value = '';
        if (this.currentResponse) {
            this.currentResponse.textData = '';
        }
    }

    /**
     * HYBRID_SINGLE_TEXT renderer - Radio + optional text
     */
    _renderHybridSingleText(container, config, existingResponse) {
        this._renderSingleChoice(container, config, existingResponse);

        const { wrapper, input } = this._createConditionalTextField('Please specify...', existingResponse);
        container.appendChild(wrapper);

        const hasConditionalText = config.options?.some(opt => opt.requiresText);
        if (hasConditionalText) {
            const selectedValue = existingResponse?.structuredData?.value;
            const selectedOption = config.options.find(opt => opt.value === selectedValue);

            if (!selectedOption?.requiresText) {
                wrapper.classList.add('discovery__option--hidden');
            }

            container.querySelectorAll('input[type="radio"]').forEach(radioInput => {
                radioInput.addEventListener('change', () => {
                    const selectedOpt = config.options.find(opt => opt.value === radioInput.value);

                    if (selectedOpt?.requiresText) {
                        wrapper.classList.remove('discovery__option--hidden');
                        input.focus();
                        if (this.onHybridSelectionChanged) {
                            this.onHybridSelectionChanged(true);
                        }
                    } else {
                        wrapper.classList.add('discovery__option--hidden');
                        this._clearConditionalTextField(input);
                        if (this.onHybridSelectionChanged) {
                            this.onHybridSelectionChanged(false);
                        }
                    }
                });
            });
        }
    }

    /**
     * HYBRID_MULTIPLE_TEXT renderer - Checkboxes + optional text
     */
    _renderHybridMultipleText(container, config, existingResponse) {
        this._renderMultipleChoice(container, config, existingResponse);

        const { wrapper, input } = this._createConditionalTextField('Other (please specify)...', existingResponse);
        container.appendChild(wrapper);

        const hasConditionalText = config.options?.some(opt => opt.requiresText);
        if (hasConditionalText) {
            const selectedOptions = existingResponse?.structuredData?.selectedOptions || [];
            const hasRequiredTextOption = selectedOptions.some(selected => {
                const opt = config.options.find(o => o.value === selected.value);
                return opt?.requiresText;
            });

            if (!hasRequiredTextOption) {
                wrapper.classList.add('discovery__option--hidden');
            }

            const checkboxInputs = container.querySelectorAll('input[type="checkbox"]');
            const updateVisibility = () => {
                const requiresText = Array.from(checkboxInputs)
                    .filter(cb => cb.checked)
                    .some(cb => config.options.find(o => o.value === cb.value)?.requiresText);

                if (requiresText) {
                    wrapper.classList.remove('discovery__option--hidden');
                } else {
                    wrapper.classList.add('discovery__option--hidden');
                    this._clearConditionalTextField(input);
                }
            };

            checkboxInputs.forEach(cb => cb.addEventListener('change', updateVisibility));
        }
    }

    /**
     * INSTRUCTION_CARD renderer - Display only
     */
    _renderInstructionCard(container, config) {
        const card = document.createElement('div');
        card.className = 'discovery__instruction';

        if (config.title) {
            const title = document.createElement('h3');
            title.className = 'discovery__instruction-title';
            title.textContent = config.title;
            card.appendChild(title);
        }

        if (config.content) {
            const content = document.createElement('div');
            content.className = 'discovery__instruction-content';
            content.innerHTML = config.content; // Allow HTML for formatting
            card.appendChild(content);
        }

        // Auto-acknowledgment for instruction cards
        this.currentResponse = {
            structuredData: { acknowledged: true }
        };

        container.appendChild(card);
    }

    /**
     * YES_NO renderer - Simple binary choice
     */
    _renderYesNo(container, config, existingResponse) {
        const options = [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' }
        ];

        const existingValue = existingResponse?.structuredData?.value;

        options.forEach((option) => {
            const optionWrapper = document.createElement('div');
            optionWrapper.className = 'discovery__option';
            if (existingValue === option.value) {
                optionWrapper.classList.add('discovery__option--selected');
            }

            const label = document.createElement('label');
            label.style.width = '100%';
            label.style.cursor = 'pointer';
            label.style.display = 'flex';
            label.style.alignItems = 'center';
            label.style.justifyContent = 'center';
            label.textContent = option.label;

            optionWrapper.appendChild(label);
            container.appendChild(optionWrapper);

            // Click handler for option selection
            optionWrapper.addEventListener('click', () => {
                // Remove selected class from all options
                container.querySelectorAll('.discovery__option').forEach(opt => {
                    opt.classList.remove('discovery__option--selected');
                });

                // Add selected class to clicked option
                optionWrapper.classList.add('discovery__option--selected');

                // Update response
                this.currentResponse = {
                    structuredData: {
                        value: option.value,
                        label: option.label
                    }
                };

                // Trigger onChange callback if set
                if (this.onChange) {
                    this.onChange();
                }

                // Trigger auto-advance callback if set
                if (this.onAutoAdvance) {
                    this.onAutoAdvance();
                }
            });
        });
    }

    /**
     * Initialize response structure based on question type
     */
    _initializeResponse(questionType) {
        switch (questionType) {
            case 'SINGLE_CHOICE':
            case 'RATING_SCALE':
            case 'YES_NO':
                return { structuredData: {} };
            case 'MULTIPLE_CHOICE':
                return { structuredData: { selectedOptions: [] } };
            case 'TEXT':
                return { textData: '' };
            case 'MATRIX':
                return { structuredData: { responses: {} } };
            case 'HYBRID_SINGLE_TEXT':
            case 'HYBRID_MULTIPLE_TEXT':
                return { structuredData: {}, textData: '' };
            case 'INSTRUCTION_CARD':
                return { structuredData: { acknowledged: true } };
            default:
                return {};
        }
    }

    /**
     * Get current response for submission
     * Returns null if the question hasn't been answered (no meaningful data)
     */
    getResponse() {
        const response = this.currentResponse;
        if (!response) return null;

        const type = this.currentQuestion?.questionType;

        // INSTRUCTION_CARD always has a response (auto-acknowledged)
        if (type === 'INSTRUCTION_CARD') {
            return response;
        }

        // Check if response has meaningful data
        let hasStructuredData = response.structuredData &&
                                 Object.keys(response.structuredData).length > 0;
        // Empty selectedOptions (default for MULTIPLE_CHOICE) is not meaningful
        if (hasStructuredData && Array.isArray(response.structuredData.selectedOptions) &&
            response.structuredData.selectedOptions.length === 0 &&
            Object.keys(response.structuredData).length === 1) {
            hasStructuredData = false;
        }
        const hasTextData = response.textData &&
                           typeof response.textData === 'string' &&
                           response.textData.trim().length > 0;

        // Return null if no meaningful data exists
        if (!hasStructuredData && !hasTextData) {
            return null;
        }

        return response;
    }

    /**
     * Validate current response
     */
    isValid() {
        if (!this.currentQuestion) return false;

        const type = this.currentQuestion.questionType;
        const response = this.currentResponse;

        // Instruction cards are always valid
        if (type === 'INSTRUCTION_CARD') return true;

        // Skip validation if question is not required
        if (!this.currentQuestion.isRequired) return true;

        switch (type) {
            case 'SINGLE_CHOICE':
            case 'RATING_SCALE':
            case 'YES_NO':
                return response?.structuredData?.value !== undefined;

            case 'MULTIPLE_CHOICE':
                return response?.structuredData?.selectedOptions?.length > 0;

            case 'TEXT':
                return response?.textData?.length > 0;

            case 'MATRIX':
                const rows = this.currentQuestion.configuration.rows || [];
                const responses = response?.structuredData?.responses || {};
                return rows.every(row => responses[row.value] !== undefined);

            case 'HYBRID_SINGLE_TEXT': {
                const hasValue = response?.structuredData?.value !== undefined;
                if (!hasValue) return false;

                // Check if selected option requires text
                const config = this.currentQuestion.configuration;
                const selectedValue = response.structuredData.value;
                const selectedOption = config.options?.find(opt => opt.value === selectedValue);

                if (selectedOption?.requiresText) {
                    return response?.textData?.length > 0;
                }
                return true;
            }

            case 'HYBRID_MULTIPLE_TEXT': {
                const hasOptions = response?.structuredData?.selectedOptions?.length > 0;
                if (!hasOptions) return false;

                // Check if any selected option requires text
                const config = this.currentQuestion.configuration;
                const selectedOptions = response.structuredData.selectedOptions;
                const requiresText = selectedOptions.some(selected => {
                    const opt = config.options?.find(o => o.value === selected.value);
                    return opt?.requiresText;
                });

                if (requiresText) {
                    return response?.textData?.length > 0;
                }
                return true;
            }

            default:
                return false;
        }
    }

    /**
     * Show validation or info messages (public method for orchestrator)
     */
    showMessage(text, type = 'info') {
        this._showMessage(text, type);
    }

    /**
     * Clear any displayed message (call when navigating between questions)
     */
    clearMessage() {
        const existingMsg = this.container.querySelector('.discovery__message');
        if (existingMsg) {
            existingMsg.remove();
        }
    }

    /**
     * Show validation or info messages (internal)
     */
    _showMessage(text, type = 'info') {
        const existingMsg = this.container.querySelector('.discovery__message');
        if (existingMsg) {
            existingMsg.remove();
        }

        const message = document.createElement('div');
        message.className = `discovery__message discovery__message--${type}`;
        message.textContent = text;

        this.container.insertBefore(message, this.container.firstChild);
        // Message persists until question changes (container cleared by render())
        // To re-enable auto-hide after 3 seconds, uncomment below:
        // setTimeout(() => {
        //     message.classList.add('discovery__message--fade');
        //     setTimeout(() => message.remove(), 300);
        // }, 3000);
    }

    /**
     * Clear the renderer
     */
    clear() {
        this.container.innerHTML = '';
        this.currentQuestion = null;
        this.currentResponse = null;
        this.questionElement = null;
    }

    /**
     * Set read-only mode for navigation review
     */
    setReadOnly(readOnly) {
        if (!this.questionElement) return;

        const inputs = this.questionElement.querySelectorAll('input, textarea, button');
        inputs.forEach(input => {
            input.disabled = readOnly;
        });

        if (readOnly) {
            this.questionElement.classList.add('discovery__question--readonly');
        } else {
            this.questionElement.classList.remove('discovery__question--readonly');
        }
    }
}
